import type {CmLogger} from "@cm/log"
import type {MaterialGraph} from "#material/nodes/base/graph/material-graph"
import {TextureFromSet} from "#material/nodes/texture/texture-from-set/texture-from-set"
import {TextureSet} from "#material/nodes/texture/texture-set/texture-set"
import {BsdfPrincipled} from "#material/nodes/shader/bsdf-principled/bsdf-principled"
import type {DataResolver} from "#material/validation/data-resolver/index"
import {GraphNode} from "@cm/graph-v3"
import {TextureFromRevision} from "#material/nodes/texture/texture-from-revision/texture-from-revision"
import {textureTypeFromLegacy} from "#material/nodes/base/material/validation/texture"

export async function getPhysicalSizeInfoForMaterialGraph(
    graph: MaterialGraph,
    resolve: DataResolver,
    logger: CmLogger,
): Promise<{widthCm: number; heightCm: number; pxPerCm: number} | undefined> {
    type ImageNode = TextureFromRevision | TextureSet | TextureFromSet
    const imageNodes = new Set<ImageNode>(
        graph.nodes.filter((node) => node instanceof TextureFromRevision || node instanceof TextureSet || node instanceof TextureFromSet),
    )
    const bsdfPrincipledNode = graph.nodes.find((node) => node instanceof BsdfPrincipled)
    const baseColorConnection = bsdfPrincipledNode?.parameters.inputs.baseColor
    const baseColorNode =
        baseColorConnection instanceof GraphNode
            ? [...baseColorConnection].find((node) => node instanceof TextureFromRevision || node instanceof TextureSet || node instanceof TextureFromSet)
            : undefined

    if (baseColorNode === undefined && imageNodes.size === 0) return undefined

    const nodeForSize = baseColorNode ?? Array.from(imageNodes)[0]

    const imageResource = await referenceImageResource(nodeForSize, resolve, logger)

    const widthCm = imageResource?.widthCm
    const heightCm = imageResource?.heightCm
    if (widthCm && heightCm) {
        let pxPerCm: number
        const dataObject = await resolve.dataObjectLegacy(imageResource.dataObjectLegacyId)
        if (!dataObject) {
            throw new Error("Failed to resolve data object")
        }
        if (dataObject.width && dataObject.height) {
            pxPerCm = Math.max(dataObject.width, dataObject.height)
            pxPerCm /= Math.max(widthCm, heightCm)
        } else {
            throw Error("Tried to get pxPerCm from data object without width and height")
        }
        return {
            widthCm,
            heightCm,
            pxPerCm,
        }
    }

    return undefined
}

async function referenceImageResource(
    nodeForSize: TextureFromRevision | TextureFromSet | TextureSet,
    resolve: DataResolver,
    logger: CmLogger,
): Promise<{dataObjectLegacyId: number; heightCm: number; widthCm: number} | null> {
    if (nodeForSize instanceof TextureFromRevision) {
        if (!nodeForSize.parameters.settings.textureRevisionLegacyId) {
            logger.warn("Texture From Revision node does not have a texture revision legacy id, cannot determine physical size")
            return null
        }
        const imageResource = await resolve.textureRevisionLegacy(nodeForSize.parameters.settings.textureRevisionLegacyId)
        if (!imageResource) {
            logger.warn(`Legacy texture revision image resource could not be resolved: ${nodeForSize.parameters.settings.textureRevisionLegacyId}`)
            return null
        }
        return {
            dataObjectLegacyId: imageResource.dataObjectLegacyId,
            widthCm: imageResource.widthCm,
            heightCm: imageResource.heightCm,
        }
    }
    if (nodeForSize instanceof TextureFromSet && nodeForSize.parameters.settings.mapAssignmentTextureType) {
        const textureSetRevisionSlot = await resolve.textureSetRevisionSlot({
            id: nodeForSize.parameters.settings.textureSetRevisionId!,
            slot: textureTypeFromLegacy(nodeForSize.parameters.settings.mapAssignmentTextureType),
        })
        if (!textureSetRevisionSlot) {
            return null
        }
        return {
            dataObjectLegacyId: textureSetRevisionSlot.dataObject.legacyId,
            heightCm: textureSetRevisionSlot.heightCm,
            widthCm: textureSetRevisionSlot.widthCm,
        }
    }
    const textureSetRevision = await resolve.textureSetRevision(nodeForSize.parameters.settings.textureSetRevisionId!)
    if (!textureSetRevision) {
        return null
    }
    const firstMapAssignment = textureSetRevision.mapAssignments?.[0]
    if (!firstMapAssignment) {
        return null
    }
    return {dataObjectLegacyId: firstMapAssignment.legacyId, heightCm: textureSetRevision.heightCm, widthCm: textureSetRevision.widthCm}
}
